/**
 * Access Requests — the experts-web rebuild of the legacy public/admin.js
 * "Requests" tab (the day-job that the 10 ops pages did NOT cover; cutover
 * blocker #48).
 *
 * Source of truth: apps/experts/api/chat/users.js (handler verified verbatim).
 *   GET  /api/chat/users?admin=true&requests=true[&status=<status>]
 *        → AccessRequest[] (created_at DESC, limit 200)
 *   POST /api/chat/users?admin=true&requests=true { request_id, action }
 *        approve → server sends the Entra invite + marks 'approved'
 *        deny    → marks 'denied'
 *
 * Behaviour replicated from the legacy admin.js faithfully:
 *   - status filter (all / pending / approved / denied)
 *   - pending badge + request count
 *   - per-card "Approve & Invite" / "Deny" actions (deny is confirmed)
 *   - "Approve All (N)" when >1 pending — approves each sequentially and reports
 *     an approved/failed tally (legacy semantics: best-effort, count failures)
 *
 * EVERY call goes through the data-access hooks → the vendored `api` wrapper →
 * the single auth resolver seam. No direct fetch, no per-page MSAL (the seam the
 * codex review demands; rebuild-lessons.md #2).
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@andavo/web-ui';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Panel,
} from '../../components/primitives';
import {
  useAccessRequestAction,
  useAccessRequests,
} from '../../data-access/useAdmin';
import { absTime } from '../../lib/format';
import type { AccessRequest, AccessRequestStatus } from '../../types';

const STATUS_FILTERS: { value: AccessRequestStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
];

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-700',
  approved: 'bg-green-500/15 text-green-700',
  denied: 'bg-red-500/15 text-red-600',
};

function StatusBadge({ status }: { status: AccessRequestStatus }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-inter font-medium text-xs ${
        STATUS_BADGE[status] ?? 'bg-content-tertiary/15 text-content-secondary'
      }`}
    >
      {status}
    </span>
  );
}

function RequestCard({ req }: { req: AccessRequest }) {
  const action = useAccessRequestAction();

  const handle = (act: 'approve' | 'deny') => {
    if (act === 'deny' && !window.confirm(`Deny access for ${req.email}?`)) {
      return;
    }
    action.mutate(
      { request_id: req.id, action: act },
      {
        onSuccess: () =>
          toast.success(
            act === 'approve'
              ? `Approved — invite sent to ${req.email}.`
              : `Denied request from ${req.email}.`,
          ),
        onError: (err) =>
          toast.error((err as Error)?.message ?? 'Action failed'),
      },
    );
  };

  const pending = req.status === 'pending';

  return (
    <div
      className="rounded-md border border-border-subtle bg-surface-foreground p-4"
      data-testid="access-request-card"
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-sans font-medium text-content-primary text-sm">
          {req.name || '(no name)'}
        </span>
        <StatusBadge status={req.status} />
      </div>
      <div className="font-inter text-content-secondary text-sm">
        {req.email}
      </div>
      {req.reason ? (
        <div className="mt-1 font-inter text-content-secondary text-sm italic">
          “{req.reason}”
        </div>
      ) : null}
      <div className="mt-1 font-inter text-content-tertiary text-xs">
        Requested {absTime(req.created_at)}
      </div>
      {pending ? (
        <div className="mt-3 flex items-center gap-2">
          <Button
            data-testid="access-request-approve"
            disabled={action.isPending}
            onClick={() => handle('approve')}
            type="button"
          >
            {action.isPending && action.variables?.action === 'approve'
              ? 'Approving…'
              : 'Approve & Invite'}
          </Button>
          <Button
            data-testid="access-request-deny"
            disabled={action.isPending}
            onClick={() => handle('deny')}
            type="button"
            variant="danger"
          >
            {action.isPending && action.variables?.action === 'deny'
              ? 'Denying…'
              : 'Deny'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function AccessRequestsPage() {
  const [status, setStatus] = useState<AccessRequestStatus | ''>('');
  const { data, isPending, isError, error, refetch, isFetching } =
    useAccessRequests(status);
  const approveAll = useAccessRequestAction();
  const [bulkRunning, setBulkRunning] = useState(false);

  const requests = data ?? [];
  const pendingRequests = useMemo(
    () => requests.filter((r) => r.status === 'pending'),
    [requests],
  );

  /**
   * Approve All — legacy semantics: approve each pending request sequentially,
   * tally approved vs. failed, then surface the result. Sequential (not
   * Promise.all) to mirror admin.js and to keep the server's per-request invite
   * sends ordered.
   */
  const handleApproveAll = async () => {
    const count = pendingRequests.length;
    if (
      count === 0 ||
      !window.confirm(`Approve all ${count} pending requests and send invites?`)
    ) {
      return;
    }
    setBulkRunning(true);
    let approved = 0;
    let failed = 0;
    for (const req of pendingRequests) {
      try {
        await approveAll.mutateAsync({
          request_id: req.id,
          action: 'approve',
        });
        approved += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkRunning(false);
    if (failed > 0) {
      toast.error(`Approved ${approved}, failed ${failed}.`);
    } else {
      toast.success(`Approved all ${approved} requests. Invites sent.`);
    }
  };

  return (
    <div data-testid="access-requests-page">
      <AdminPageHeader
        actions={
          <div className="flex items-center gap-2">
            {pendingRequests.length > 1 ? (
              <Button
                data-testid="access-request-approve-all"
                disabled={bulkRunning}
                onClick={() => void handleApproveAll()}
                type="button"
              >
                {bulkRunning
                  ? 'Approving…'
                  : `Approve All (${pendingRequests.length})`}
              </Button>
            ) : null}
            <button
              className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
              onClick={() => void refetch()}
              type="button"
            >
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        }
        subtitle="Approve & invite or deny inbound access requests to Andavo Experts."
        title="Access Requests"
      />

      <div className="mb-4 flex items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            className={`rounded-md px-2.5 py-1 font-inter text-sm transition-colors ${
              status === f.value
                ? 'bg-surface-action-subtle text-content-action'
                : 'text-content-secondary hover:bg-surface-background'
            }`}
            key={f.value || 'all'}
            onClick={() => setStatus(f.value)}
            type="button"
          >
            {f.label}
          </button>
        ))}
        {data ? (
          <span
            className="ml-auto font-inter text-content-tertiary text-xs"
            data-testid="access-request-count"
          >
            {requests.length} request{requests.length === 1 ? '' : 's'}
            {pendingRequests.length > 0
              ? ` · ${pendingRequests.length} pending`
              : ''}
          </span>
        ) : null}
      </div>

      <Panel>
        {isPending ? <AdminLoading /> : null}
        {isError ? (
          <AdminError
            message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
          />
        ) : null}
        {data && requests.length === 0 ? (
          <AdminEmpty>No access requests found.</AdminEmpty>
        ) : null}
        {data && requests.length > 0 ? (
          <div className="flex flex-col gap-3">
            {requests.map((req) => (
              <RequestCard key={req.id} req={req} />
            ))}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

export default AccessRequestsPage;
